import { IconButton, Box, FormControl, Autocomplete, TextField, Grid, Typography, Button } from "@mui/material";
import SearchForm from "../DashBoard/DashboardSearchForm";
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useState } from "react";
import PaginationControl from "../Layout/Pagination";
import "../Layout/Pagination/index.css";
import FullPageLoader from "../Shared/FullPageLoader";
import useStore from "../../store/store";
import { RECORDS_PER_PAGE } from '../../constants/index';
import { debounce } from 'lodash';
import { AppointmentServiceInternal } from "../../services/appointment.service";
import ITPTable from "./ITPTable";
import { CorrectionalFacilityServiceInternal } from "../../services/correctionFacilities.service";
import { AppointmentSearchRequest } from "../../types";
import { useNavigate } from "react-router-dom";

const recordsPerPage = RECORDS_PER_PAGE;

interface Facility {
  id: number;
  officer_name: string;
  name: string;
  timeZone: string;
}

function ITPUserDashboard() {
  const user: any = useStore();
  const [data, setData] = useState([]);
  const [currentFacility, setCurrentFacility] = useState<Facility | null>(null);
  const [refreshForFacilityUpdate, setRefreshForFacilityUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [userFacilities, setUserFacilities] = useState<Facility[] | null>(null);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);
  const [refresh, setRefresh] = useState<boolean>(false);
  const paginatedData = data?.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (search: string) => {
    setSearch(search);
  };
  const debouncedSearchChange = debounce(handleSearchChange, 500);

  const handleSearchChangeDebounced = (search: string) => {
    debouncedSearchChange(search);
  };

  const handleStartDateChange = (date: string) => {
    const formateDate = new Date(date);
    setStartDate(formateDate);
  };

  const handleEndDateChange = (dateString: string) => {
    const date = new Date(dateString);
    setEndDate(date);
  };

  const handleFacilityChange = (id: number) => {
    // let selectedFacilityId = parseInt(event.target.value, 10);
    let selectedFacility = userFacilities?.find(f => f.id === id);
    setCurrentFacility(selectedFacility ?? null);
    setRefreshForFacilityUpdate(true);
  }

  const fetchDataFromApi = async () => {
    try {
      setIsLoading(true);
      const today = new Date();
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);

      let payload: AppointmentSearchRequest = {
        startDate: startDate?.toISOString() ?? today.toISOString(),
        endDate: endDate?.toISOString() ?? nextWeek.toISOString(),
        userDob: null,
        searchQuery: search,
        stateSearchQuery: "",
        correctionalFacilityId: currentFacility?.id ?? null
      }

      const response = await AppointmentServiceInternal.getappointment(payload);

      if (response?.data.isSuccess) {
        setData(response.data.data);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching data from API:', error);
    }
  }

  const navigate = useNavigate();
  const handleClick = (e: any) => {
    navigate("/new-appointment");
  };

  const getFullName = () => {
    const firstName = user.getUser() && user.getUser().first_name ? user.getUser().first_name : '';
    const lastName = user.getUser() && user.getUser().last_name ? user.getUser().last_name : '';
    return `${firstName} ${lastName}`;
  };

  useEffect(() => {
    const newStartIndex = (currentPage - 1) * recordsPerPage;
    const newEndIndex = newStartIndex + recordsPerPage;
    setStartIndex(newStartIndex);
    setEndIndex(Math.min(newEndIndex, data.length));
  }, [currentPage, data, recordsPerPage]);

  useEffect(() => {
    fetchDataFromApi();
    setCurrentPage(1);
  }, [search, startDate, endDate]);

  useEffect(() => {
    if (refresh) {
      fetchDataFromApi();
      setCurrentPage(1);
      setRefresh(false);
    }
  }, [refresh]);

  useEffect(() => {
    if (refreshForFacilityUpdate && currentFacility) {
      setRefresh(true);
      setRefreshForFacilityUpdate(false);
    }
  }, [currentFacility, refreshForFacilityUpdate]);

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const response = await CorrectionalFacilityServiceInternal.getCorrectionalFacilities();
        if (response?.data.isSuccess) {
          const defaultFacility: Facility = {
            id: 0,
            officer_name: "",
            name: "All Facilities",
            timeZone: ""
          };
          const facilities: Facility[] = response.data.data;
          facilities.sort((a, b) => {
            if (a.name < b.name) {
              return -1;
            }
            if (a.name > b.name) {
              return 1;
            }
            return 0;
          });
          facilities.splice(0, 0, defaultFacility);
          setCurrentFacility(defaultFacility);
          setUserFacilities(facilities);
        }
      } catch (error) {
        setIsLoading(false);
        console.error('Error fetching facilities:', error);
      }
    }
    fetchFacilities();
  }, []);

  return (
    <div className="patient-dashboard">
      <Box className="dashboard-filter" sx={{ width: "100%", mb: 2 }}>
        <Grid container alignItems="center" justifyContent="flex-start" spacing={1}>
          <Grid item xs={12}>
            <h1 className="text-3xl">ITP Dashboard</h1>
          </Grid>
          <Grid item xs={12} sx={{ ml: 2 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Typography variant="body1">
                  <strong>ITP User Name:</strong> {getFullName()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Box display="flex" alignItems="center">
                  <Typography variant="body1">
                    <strong>Correctional Facility:&nbsp;&nbsp;</strong>
                  </Typography>
                  {userFacilities && currentFacility && (
                    <>
                      {userFacilities.length > 1 ? (
                        <FormControl fullWidth size="small" sx={{ maxWidth: "50%" }}>
                          <Autocomplete
                            size="small"
                            options={userFacilities}
                            value={currentFacility}
                            onChange={(event, newValue) => {
                              if (newValue) handleFacilityChange(newValue.id);
                            }}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                              <TextField {...params} variant="outlined" />
                            )}
                            renderOption={(props, option) => (
                              <li {...props}>
                                <Box
                                  component="span"
                                  title={option.name}
                                  sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: "100%",
                                  }}
                                >
                                  {option.name}
                                </Box>
                              </li>
                            )}
                          />
                        </FormControl>
                      ) : (
                        <Typography variant="body1" sx={{ ml: 1 }}>
                          {currentFacility?.name}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <div className="flex relative">
        <div className="justify-between flex">
          <span>
            <IconButton
              aria-label="search"
              size="large"
              className="p-2"
              disabled
            >
              <SearchIcon fontSize="inherit" />
            </IconButton>
          </span>
          <SearchForm
            onSearch={handleSearchChangeDebounced}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
          />
        </div>
        <Box className="justify-end absolute right-0 sm:right-full sm:left-8 sm:top-12 sm:w-full xs:right-full xs:left-7 xs:w-36 xs:top-36 xs:pt-2">
          <Button
            variant="contained"
            style={{
              backgroundColor: "#6d87ff",
            }}
            onClick={handleClick}
          >
            <AddIcon /> New Appointment
          </Button>
        </Box>
      </div>
      {isLoading && <FullPageLoader />}
      <>
        <div className="mt-5 overflow-auto">
          <ITPTable
            data={paginatedData ?? []}
            setRefresh={setRefresh}
            setIsLoading={setIsLoading}
          />
        </div>
        {paginatedData?.length > 0 && data && (
          <PaginationControl
            totalRecords={data?.length}
            recordsPerPage={recordsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </>

    </div>
  );
}
export default ITPUserDashboard;